import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  createQuotation,
  fetchAllQuotations,
  getAllItemsApi,
  getQuotationLogs,
  getQuotationNumber,
  updateQuotationApi,
} from "../../api/Lead.api";
import {
  IItemOption,
  IQuotationData,
  IQuotationLog,
} from "../../models/quotationData.model";
import { fetchCustomerQuotations } from "../../api/customer.api";
import { IProduct } from "../../models/productData.model";

interface QuotationSliceState {
  quotations: IQuotationData[];
  quotationLogs: IQuotationLog[];
  totalQuotations: number;
  loading: boolean;
  isCreating: boolean; // For Create
  isUpdating: boolean; // For Update
  error?: string | null;
  quotationNumber: string;
  isQuotationEmpty: boolean;
  itemOptions: IItemOption[];
  itemOptionsLoading: boolean;
}

const initialState: QuotationSliceState = {
  quotations: [],
  totalQuotations: 0,
  loading: false,
  isCreating: false,
  isUpdating: false,
  error: null,
  quotationNumber: "",
  quotationLogs: [],
  isQuotationEmpty: true,
  itemOptions: [],
  itemOptionsLoading: false,
};

// Fetch Quotation Data
export const getAllQuotations = createAsyncThunk(
  "leads/getAllQuotations",
  async (
    { leadId, page }: { leadId: string; page: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchAllQuotations(leadId, page);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch lead quotations",
      );
    }
  },
);

// Thunk to fetch customer quotations
export const getCustomerQuotations = createAsyncThunk(
  "customers/fetchCustomerQuotations",
  async (customerId: string, { rejectWithValue }) => {
    try {
      const response = await fetchCustomerQuotations(customerId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch EPC quotations",
      );
    }
  },
);

export const addQuotation = createAsyncThunk(
  "leads/addQuotation",
  async (
    {
      bookedProducts,
      leadId,
      transportPrice,
      discountPrice,
      customGst,
    }: {
      bookedProducts: IProduct[];
      leadId: string;
      transportPrice: number;
      discountPrice: number;
      customGst?: number | null;
    },

    { rejectWithValue },
  ) => {
    try {
      const quotationResponse = await createQuotation(
        bookedProducts,
        leadId,
        transportPrice,
        discountPrice,
        customGst,
      );
      return quotationResponse;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const updateQuotation = createAsyncThunk(
  "leads/updateQuotation",
  async (
    params: {
      leadId: string;
      quotationId: string;
      products: IProduct[];
      transportPrice: number;
      discountPrice: number;
      oldQuotation?: IQuotationData;
    },
    { rejectWithValue },
  ) => {
    try {
      const data = await updateQuotationApi(
        params.leadId,
        params.quotationId,
        params.products,
        params.transportPrice,
        params.discountPrice,
        params.oldQuotation,
      );
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update quotation",
      );
    }
  },
);

export const fetchQuotationNumber = createAsyncThunk(
  "leads/getQuotationNumber",
  async (quotationId: string, { rejectWithValue }) => {
    try {
      const response = await getQuotationNumber(quotationId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch quotation number",
      );
    }
  },
);

export const fetchItemOptions = createAsyncThunk(
  "quotation/fetchItemOptions",
  async (
    params: { page?: number; limit?: number; search?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await getAllItemsApi(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch item options",
      );
    }
  },
);

export const fetchQuotationLogs = createAsyncThunk<
  IQuotationLog[],
  string,
  { rejectValue: string }
>("leads/fetchQuotationLogs", async (quotationId, { rejectWithValue }) => {
  try {
    const response = await getQuotationLogs(quotationId);
    return response.data.logs as IQuotationLog[];
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message ?? "Failed to fetch quotation logs",
    );
  }
});

const quotationSlice = createSlice({
  name: "quotation",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllQuotations.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllQuotations.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations = action.payload.data || [];
        state.totalQuotations = action.payload.pagination.totalPages;
        // state.product = action.payload.productMap || {};
        state.isQuotationEmpty = action.payload.data.length === 0;
      })
      .addCase(getAllQuotations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getCustomerQuotations.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCustomerQuotations.fulfilled, (state, action) => {
        state.loading = false;
        state.quotations = action.payload || [];
        state.isQuotationEmpty = action.payload.length === 0;
        // state.product = action.payload.productMap || {};
      })
      .addCase(getCustomerQuotations.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(addQuotation.pending, (state) => {
        state.isCreating = true;
      })
      .addCase(addQuotation.fulfilled, (state, action) => {
        state.isCreating = false;
        state.quotations.push(action.payload); // Push new quotation correctly
      })
      .addCase(addQuotation.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      .addCase(updateQuotation.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateQuotation.fulfilled, (state, action) => {
        state.isUpdating = false;
        const updated = action.payload;

        const index = state.quotations.findIndex(
          (q: any) => q._id === updated._id,
        );

        if (index !== -1) {
          state.quotations[index] = {
            ...state.quotations[index],
            ...updated,
          };
        }
      })
      .addCase(updateQuotation.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      .addCase(fetchQuotationNumber.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchQuotationNumber.fulfilled, (state, action) => {
        state.loading = false;
        // Update your state as needed - you might want to add a quotationNumber field to your state
        state.quotationNumber = action.payload.quotationNumber;
      })
      .addCase(fetchQuotationNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchQuotationLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuotationLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.quotationLogs = action.payload;
      })
      .addCase(fetchQuotationLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch quotation logs";
      })
      .addCase(fetchItemOptions.pending, (state) => {
        state.itemOptionsLoading = true;
      })
      .addCase(fetchItemOptions.fulfilled, (state, action) => {
        state.itemOptionsLoading = false;
        const options: IItemOption[] = action.payload.data?.itemOptions || [];
        // append when paginating, replace on a fresh search/first page
        state.itemOptions =
          (action.payload.pagination?.page ?? 1) > 1
            ? [...state.itemOptions, ...options]
            : options;
      })
      .addCase(fetchItemOptions.rejected, (state, action) => {
        state.itemOptionsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default quotationSlice.reducer;
